import { PLAN_PRICE_USD_PER_SEAT } from '@/config/planFeatures';

export interface BillingStateShape {
  planState?: string;
  primaryCta?: 'subscribe' | 'manage' | 'contact_sales' | 'none';
  bannerMessage?: string | null;
  bannerSeverity?: 'info' | 'warning' | 'error' | null;
  needsPlanPayment?: boolean;
  planDaysLeft?: number | null;
  planPriceUsdPerSeat?: number;
  hasPaidSubscription?: boolean;
}

export interface BillingCTA {
  bannerMessage: string | null;
  bannerSeverity: 'info' | 'warning' | 'error' | null;
  primaryLabelKey: string | null;
  primaryLabelParams?: Record<string, unknown>;
  primaryAction: 'subscribe' | 'manage' | 'contact_sales' | null;
  secondaryLabelKey: string | null;
  secondaryAction: 'contact_sales' | null;
  showManageBilling: boolean;
}

export interface BillingAccessShape {
  isComped?: boolean;
  paymentRequired?: boolean;
  suspendReason?: string | null;
  symbolicAmountPaise?: number;
  displayMonthlyUsd?: number;
}

export function resolveBillingCTA(
  billingState?: BillingStateShape | null,
  hasActiveBilling?: boolean,
  opts?: { billingConfigured?: boolean; billingAccess?: BillingAccessShape | null },
): BillingCTA {
  const price = billingState?.planPriceUsdPerSeat ?? PLAN_PRICE_USD_PER_SEAT;
  const state = billingState?.planState ?? 'trial_local';
  const primary = billingState?.primaryCta ?? 'none';
  const billingConfigured = opts?.billingConfigured ?? false;
  const billingAccess = opts?.billingAccess;

  const base: BillingCTA = {
    bannerMessage: billingState?.bannerMessage ?? billingAccess?.suspendReason ?? null,
    bannerSeverity: billingState?.bannerSeverity ?? (billingAccess?.paymentRequired ? 'warning' : null),
    primaryLabelKey: null,
    primaryAction: null,
    secondaryLabelKey: 'billing.cta.contactSalesEnterprise',
    secondaryAction: 'contact_sales',
    showManageBilling: !!billingState?.hasPaidSubscription && !!hasActiveBilling,
  };

  if (billingAccess?.isComped && !billingAccess.paymentRequired) {
    return {
      ...base,
      bannerMessage: null,
      bannerSeverity: null,
      primaryLabelKey: null,
      primaryAction: null,
      secondaryLabelKey: null,
      secondaryAction: null,
    };
  }

  if (primary === 'subscribe' || state === 'trial_ended' || billingState?.needsPlanPayment) {
    return {
      ...base,
      primaryLabelKey: 'billing.cta.startBilling',
      primaryLabelParams: { price },
      primaryAction: 'subscribe',
    };
  }

  if (primary === 'manage' || state === 'paid') {
    return {
      ...base,
      primaryLabelKey: 'billing.cta.manageSubscription',
      primaryAction: 'manage',
      secondaryLabelKey: 'billing.cta.contactSalesEnterprise',
    };
  }

  if (primary === 'contact_sales' || state === 'enterprise') {
    if (billingAccess?.isComped) {
      return {
        ...base,
        bannerMessage: null,
        bannerSeverity: null,
        primaryLabelKey: null,
        primaryAction: null,
        secondaryLabelKey: null,
        secondaryAction: null,
      };
    }
    return {
      ...base,
      primaryLabelKey: 'billing.cta.contactSales',
      primaryAction: 'contact_sales',
      secondaryLabelKey: null,
      secondaryAction: null,
    };
  }

  if (state === 'trial_local' || state === 'trialing_billing') {
    if (billingConfigured) {
      return {
        ...base,
        primaryLabelKey: 'billing.cta.subscribeEarly',
        primaryLabelParams: { price },
        primaryAction: 'subscribe',
      };
    }
    return {
      ...base,
      primaryLabelKey: null,
      primaryAction: null,
    };
  }

  return base;
}
